import { useState } from 'react'
import {
  Button,
  CollapsibleCard,
  IconButton,
  Input,
  OrderButtons,
  SectionLabel,
} from '../../../design/components'
import { moved } from '../../../lib/order'
import { useCaseSheet } from '../../cases/queries'
import { ComicPageRow, type ReferenceImage } from './ComicPageRow'
import {
  availablePageTypes,
  pageTypeLabel,
  type ComicCoreSection,
  type ComicScene,
  type ComicScenesSection,
  type PageType,
} from '../types'
import type { ComicEditing } from '../useComicEditing'
import styles from './comics.module.css'

/**
 * The comic's scenes, in order, with their pages under each.
 *
 * A comic scene is a thin thing — a name and a pointer at the case scene it dramatizes —
 * so the card is mostly a frame around the pages, which is where the work happens.
 *
 * **A scene's `sceneTitle` rides on the section**, so nothing here reads the case sheet to
 * render a row. The case is read for one purpose only: offering the scenes that may still
 * be added, which is a list this comic does not carry.
 *
 * Open by default, unlike the scene tracker's panels: there the panels are one card among
 * eight, here the scenes *are* the comic. Everything else on the sheet is a heading and a
 * delete button, so a comic that opens showing none of itself is the worse default.
 */
export function ComicScenesCard({
  core,
  scenes,
  editing,
}: {
  core: ComicCoreSection | undefined
  scenes: ComicScenesSection | undefined
  editing: ComicEditing
}) {
  const [adding, setAdding] = useState(false)

  if (scenes === undefined) return null

  const all = scenes.scenes ?? []
  const pageCount = all.reduce((total, scene) => total + scene.pages.length, 0)

  // Every image in the comic, offered as a reference to draw the next page from. Built
  // once here rather than per page: a page's own art is as valid a reference as any
  // other's, and the list is the same for all of them.
  const references: ReferenceImage[] = all.flatMap((scene) =>
    scene.pages.flatMap((page, index) =>
      page.images.map((image) => ({
        id: image.id ?? 0,
        url: image.urlThumb ?? image.urlSmall ?? image.url ?? '',
        label: `${scene.name}, ${page.type === 'Page' ? `page ${index + 1}` : pageTypeLabel(page.type).toLowerCase()}`,
      })),
    ),
  )

  return (
    <CollapsibleCard
      variant="magic"
      label="Scenes"
      defaultOpen
      summary={
        all.length === 0
          ? 'none yet'
          : `${all.length} ${all.length === 1 ? 'scene' : 'scenes'} · ${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`
      }
      action={
        editing.canEdit && !adding ? (
          <IconButton label="Add a scene" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {all.length === 0 && !adding && (
        <p className={styles.empty}>
          {editing.canEdit
            ? 'A comic scene dramatizes one scene of the case. Add one to start drawing.'
            : 'Nothing has been drawn yet.'}
        </p>
      )}

      {all.map((scene, index) => (
        <SceneBlock
          key={scene.id}
          scene={scene}
          index={index}
          total={all.length}
          allIds={all.map((entry) => entry.id)}
          core={core}
          references={references}
          editing={editing}
        />
      ))}

      {editing.canEdit && adding && core !== undefined && (
        <AddSceneForm core={core} editing={editing} onClose={() => setAdding(false)} />
      )}
    </CollapsibleCard>
  )
}

function SceneBlock({
  scene,
  index,
  total,
  allIds,
  core,
  references,
  editing,
}: {
  scene: ComicScene
  index: number
  total: number
  allIds: number[]
  core: ComicCoreSection | undefined
  references: ReferenceImage[]
  editing: ComicEditing
}) {
  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(scene.name)
  const [addingPage, setAddingPage] = useState(false)

  return (
    <div className={styles.row}>
      <div className={styles.rowHead}>
        <span className={styles.rowName}>{scene.name}</span>

        {editing.canEdit && (
          <span className={styles.orderButtons}>
            <OrderButtons
              label={`scene ${index + 1}`}
              index={index}
              total={total}
              disabled={editing.reorderScenes.isPending}
              onMove={(to) => editing.reorderScenes.mutate({ ids: moved(allIds, index, to) })}
            />
            <Button
              variant="danger"
              disabled={editing.removeScene.isPending}
              onClick={() => editing.removeScene.mutate({ comicSceneId: scene.id })}
            >
              Remove
            </Button>
          </span>
        )}
      </div>

      {/* The case scene this dramatizes, carried on the section so no lookup is needed. */}
      {scene.sceneTitle !== null && scene.sceneTitle !== '' && (
        <span className={styles.rowMeta}>From “{scene.sceneTitle}”</span>
      )}

      {renaming ? (
        <form
          className={styles.inlineForm}
          onSubmit={(event) => {
            event.preventDefault()
            editing.renameScene.mutate(
              { comicSceneId: scene.id, name },
              { onSuccess: () => setRenaming(false) },
            )
          }}
        >
          <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
          <div className={styles.actions}>
            <Button
              variant="secondary"
              onClick={() => {
                setName(scene.name)
                setRenaming(false)
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={editing.renameScene.isPending || name.trim() === ''}>
              {editing.renameScene.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      ) : (
        editing.canEdit && (
          <div className={styles.rowActions}>
            <Button variant="secondary" onClick={() => setRenaming(true)}>
              Rename
            </Button>
            {!addingPage && (
              <Button variant="secondary" onClick={() => setAddingPage(true)}>
                Add a page
              </Button>
            )}
          </div>
        )
      )}

      {scene.pages.length === 0 && !addingPage && <p className={styles.empty}>No pages yet.</p>}

      {scene.pages.map((page, pageIndex) => (
        <ComicPageRow
          key={page.id}
          page={page}
          index={pageIndex}
          scene={scene}
          editing={editing}
          references={references}
        />
      ))}

      {editing.canEdit && addingPage && (
        <AddPageForm
          comicSceneId={scene.id}
          core={core}
          editing={editing}
          onClose={() => setAddingPage(false)}
        />
      )}
    </div>
  )
}

/**
 * Adding a scene: a name, and the case scene it dramatizes.
 *
 * The picker offers **the case's own scenes**, because the API refuses any other — a comic
 * of one case dramatizing another's scenes would be nonsense the foreign key alone would
 * allow. That is one read of the case this comic already names, and it is the only reason
 * this card touches the case at all.
 */
function AddSceneForm({
  core,
  editing,
  onClose,
}: {
  core: ComicCoreSection
  editing: ComicEditing
  onClose: () => void
}) {
  const caseQuery = useCaseSheet(core.caseId)
  const caseScenes = caseQuery.data?.sections.scenes?.scenes ?? []

  const [name, setName] = useState('')
  const [sceneId, setSceneId] = useState<number | null>(null)

  const chosen = sceneId ?? caseScenes[0]?.id ?? null

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        if (chosen === null) return

        editing.addScene.mutate(
          { name, sceneId: chosen },
          {
            onSuccess: () => {
              setName('')
              onClose()
            },
          },
        )
      }}
    >
      <SectionLabel tone="ember" micro>
        New scene
      </SectionLabel>

      <Input
        label="Name"
        value={name}
        autoFocus
        hint="Unique within the comic."
        onChange={(event) => setName(event.target.value)}
      />

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Dramatizes
        </SectionLabel>
        <select
          className={styles.select}
          value={chosen ?? ''}
          onChange={(event) => setSceneId(Number(event.target.value))}
        >
          {caseScenes.map((scene) => (
            <option key={scene.id} value={scene.id}>
              {scene.title}
            </option>
          ))}
        </select>
      </label>

      {caseQuery.isSuccess && caseScenes.length === 0 && (
        <p className={styles.note}>
          This comic’s case has no scenes yet. Write one on the case first.
        </p>
      )}

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={editing.addScene.isPending || name.trim() === '' || chosen === null}
        >
          {editing.addScene.isPending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  )
}

/**
 * Adding a page.
 *
 * The kind picker offers only the kinds still available: a comic holds at most one front
 * cover and one back cover across *all* its scenes, and `core` ships both flags precisely
 * so the option disappears rather than being offered and refused with a `409`.
 */
function AddPageForm({
  comicSceneId,
  core,
  editing,
  onClose,
}: {
  comicSceneId: number
  core: ComicCoreSection | undefined
  editing: ComicEditing
  onClose: () => void
}) {
  const available = availablePageTypes(core)
  const [type, setType] = useState<PageType>(available[0] ?? 'Page')
  const [script, setScript] = useState('')

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        editing.addPage.mutate(
          { comicSceneId, type, script: script === '' ? null : script },
          {
            onSuccess: () => {
              setScript('')
              onClose()
            },
          },
        )
      }}
    >
      <SectionLabel tone="ember" micro>
        New page
      </SectionLabel>

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Kind
        </SectionLabel>
        <select
          className={styles.select}
          value={type}
          onChange={(event) => setType(event.target.value as PageType)}
        >
          {available.map((option) => (
            <option key={option} value={option}>
              {pageTypeLabel(option)}
            </option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <SectionLabel tone="muted" micro>
          Script
        </SectionLabel>
        <textarea
          className={styles.textarea}
          value={script}
          maxLength={4000}
          onChange={(event) => setScript(event.target.value)}
        />
      </label>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={editing.addPage.isPending}>
          {editing.addPage.isPending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  )
}
